import { useMemo } from 'react';
import { ImageType } from '../../../gqlCommon/enum/ImageType';
import { nextFeatureSelectionIdVar } from '../../../gqlCommon/graphql/cache';
import CategoryPurpose from '../../enum/CategoryPurpose';
import similarStockCarsService from '../../service/similarStockCarsService';
import { useHistory } from 'react-router-dom';
import * as queryString from 'query-string';
import useCustomizeCarContext from '../useCustomizeCarContext';
import useUrlSearchParams from '../useUrlSearchParams';
import { UrlSearchParamType } from '../../enum/UrlSearchParamType';
import useSalesEnablementContext from '../../../gqlCommon/hooks/useSalesEnablementContext';
import { createBasePath } from '../../utils/carCodeHelpers';
import useWidgetContext from '../../../common/react/useWidgetContext';
import { ICarCardProps } from '../../components/StockLite/CarCard/CarCard';
import { ICarStockMatch } from '../../../common/model/ICarStockMatch';

const useStockCars = () => {
  const { createVehicleConfigurationUrl, searchParams } = useUrlSearchParams();
  const { isInSalesEnablement } = useSalesEnablementContext();
  const {
    configuration: { basePath },
  } = useWidgetContext();
  const { selectedCar, loadCustomizeCar } = useCustomizeCarContext();
  const { similarStockCars, car } = selectedCar || {};
  const carName = car?.attributes?.vehicleDisplayName || '';
  const { learnMoreLink } = car?.attributes || {};
  const history = useHistory();
  const {
    location: { search },
  } = history;

  const elements = useMemo(
    () =>
      (similarStockCars || []).map(
        ({
          carId,
          prices,
          images,
          vin: variantId,
          keyFeatures,
          carMatchLabel,
          selectedFeatures: { selected: selectedDefault },
          finance,
          disclaimer,
        }) => {
          const imageURL = images.find(img => img.type === ImageType.FRONT)?.url || '';
          const interiorColor = similarStockCarsService.getFeatureFromCode(
            selectedDefault,
            selectedCar,
            CategoryPurpose.INTERIOR_COLOUR,
          )?.label;
          const exteriorColor = similarStockCarsService.getFeatureFromCode(
            selectedDefault,
            selectedCar,
            CategoryPurpose.EXTERIOR_COLOUR,
          )?.label;
          const roofAndMirror = similarStockCarsService.getFeatureFromCode(
            selectedDefault,
            selectedCar,
            CategoryPurpose.ROOFANDMIRROR,
          )?.label;
          const accessory = similarStockCarsService.getFeatureFromCode(
            selectedDefault,
            selectedCar,
            CategoryPurpose.ACCESSORY,
          )?.label;
          const { selected, added, removed } = selectedCar?.selectedFeatures;
          const previousSelectedFeatures = similarStockCarsService.getSelectedFeaturesWithoutColors(
            selectedCar,
            selected,
          );
          const addedWithoutColors = similarStockCarsService.getSelectedFeaturesWithoutColors(selectedCar, added);
          const hasDisclaimer = !!disclaimer;

          return {
            carId,
            title: carName,
            carImage: {
              url: imageURL,
              alt: variantId,
            },
            features: [
              ...(exteriorColor ? [exteriorColor] : []),
              ...(interiorColor ? [interiorColor] : []),
              ...(roofAndMirror ? [roofAndMirror] : []),
              ...(accessory ? [accessory] : []),
              ...keyFeatures.map(f => f?.shortname),
            ],
            prices: prices?.totalPrice,
            stockType: prices.source,
            stockMatch: carMatchLabel.includes(ICarStockMatch.EXACT.toLowerCase())
              ? ICarStockMatch.EXACT
              : ICarStockMatch.NEAR,
            carMatchLabel,
            finance,
            hasDisclaimer,
            onSelectCar: () => {
              const selectedFeatures = [...new Set([...selectedDefault, ...previousSelectedFeatures])];
              const selectedFeatureOptions = queryString.parse(search);
              selectedFeatureOptions.selectedFeatures = selectedFeatures;
              selectedFeatureOptions.oldSelectedFeatures = selectedFeatures;
              const addedFeatures = [...new Set([...selectedFeatures, ...addedWithoutColors])];

              const vehicleConfigurationUrl = isInSalesEnablement
                ? window.location.origin +
                  createBasePath(basePath) +
                  createVehicleConfigurationUrl({ [UrlSearchParamType.CAR_CODE]: carId }, searchParams)
                : null;

              loadCustomizeCar({
                carId: carId,
                selectedFeatureIds: {
                  added: addedFeatures,
                  selected: selectedFeatures,
                  removed,
                },
                vehicleConfigurationUrl: vehicleConfigurationUrl,
              });

              nextFeatureSelectionIdVar(null);
            },
            learnMoreLink,
          } as ICarCardProps;
        },
      ),
    [
      similarStockCars,
      selectedCar,
      carName,
      search,
      searchParams,
      isInSalesEnablement,
      createVehicleConfigurationUrl,
      loadCustomizeCar,
    ],
  );

  return elements;
};

export default useStockCars;
